import { z } from 'zod';
import { RoleKeySchema } from '../roles.js';

export const UserStatusSchema = z.enum([
  'active',
  'invited',
  'disabled',
  'pending_verification',
  // NMLS verification failed / couldn't run at signup — account created but held,
  // cannot sign in, until a super-admin manually verifies the NMLS and approves.
  'pending_nmls_review',
]);
export type UserStatus = z.infer<typeof UserStatusSchema>;

/** Body of POST /api/auth/verify-email — single-use token from the welcome email. */
export const EmailVerifyInputSchema = z.object({
  token: z.string().min(20).max(128),
});
export type EmailVerifyInput = z.infer<typeof EmailVerifyInputSchema>;

export const ResendVerificationInputSchema = z.object({
  email: z.string().email(),
});
export type ResendVerificationInput = z.infer<typeof ResendVerificationInputSchema>;

/**
 * Only users with an email at this domain may sign up. Existing demo
 * accounts (`*@mana.test`) keep working at the data layer but cannot be
 * re-created via the sign-up form.
 */
export const ALLOWED_SIGNUP_EMAIL_DOMAIN = 'manahomeloans.com';

const isAllowedSignupEmail = (email: string): boolean =>
  email.toLowerCase().endsWith('@' + ALLOWED_SIGNUP_EMAIL_DOMAIN.toLowerCase());

/**
 * Single-tenant deployment: the whole company shares ONE organization. Signups
 * join this org (rather than each minting its own) so a super-admin can manage
 * everyone from Administration → Users. The id matches the row created by the DB
 * seed; the consolidation migration renames it to the company name below.
 */
export const CANONICAL_ORGANIZATION_ID = 'org_seed';
export const CANONICAL_ORGANIZATION_NAME = 'MANA HOME LOANS LLC';

/**
 * NMLS = Nationwide Multistate Licensing System number.
 * Digits only. We accept 6-12 digits to be liberal (legacy values are 6 digits;
 * newer ones up to ~9). Empty string is normalized to undefined at the boundary.
 */
const nmlsInner = z
  .string()
  .trim()
  .regex(/^\d{6,12}$/, 'NMLS must be 6+ digits (numbers only).');

export const NmlsOptionalSchema = z.union([
  nmlsInner,
  z.literal('').transform(() => undefined),
  z.undefined(),
]);

/**
 * Placeholder NMLS used when a Loan Officer omits NMLS at signup or clears it
 * via profile edit. Admins can overwrite to the real value at any time via
 * Administration → Users → NMLS. We treat this value as "unset" everywhere in
 * the UI (banner shows, My-loans tab returns 0 rows by design).
 */
export const PLACEHOLDER_NMLS = '0000000';

/**
 * Coerce an optional NMLS to a persistable value:
 *   - missing / empty / placeholder → PLACEHOLDER_NMLS
 *   - validated digits             → as-is
 */
export const normalizeNmlsForPersistence = (input: string | undefined | null): string =>
  !input || !input.trim() ? PLACEHOLDER_NMLS : input.trim();

export const UserSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string().min(1).max(120),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  nmls: z.string().nullable().optional(),
  role: RoleKeySchema,
  status: UserStatusSchema,
  lastLoginAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type User = z.infer<typeof UserSchema>;

export const SignInRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
});
export type SignInRequest = z.infer<typeof SignInRequestSchema>;

/**
 * Complex password policy — enforced on the server (PasswordSchema below feeds
 * SignUpRequestSchema) and shown live in the sign-up form (PASSWORD_RULES) so
 * the user sees exactly which requirements are still unmet.
 */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_RULES: ReadonlyArray<{ label: string; test: (p: string) => boolean }> = [
  { label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (p) => p.length >= PASSWORD_MIN_LENGTH },
  { label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'A number', test: (p) => /\d/.test(p) },
  { label: 'A special character', test: (p) => /[^A-Za-z0-9]/.test(p) },
];
export function passwordIssues(password: string): string[] {
  return PASSWORD_RULES.filter((r) => !r.test(password)).map((r) => r.label);
}
export const PasswordSchema = z
  .string()
  .max(200)
  .refine((p) => passwordIssues(p).length === 0, {
    message:
      `Password must be at least ${PASSWORD_MIN_LENGTH} characters and include an uppercase letter, ` +
      'a lowercase letter, a number, and a special character.',
  });

/**
 * Sign-up captures NMLS for loan officers up front so the Pipeline page's
 * "My loans" tab works without a follow-up step. NMLS is optional only for
 * users who sign up as an admin without ever originating loans.
 *
 * Email must end in @manahomeloans.com — enforced by Zod refinement so the
 * API auto-rejects with a 400 before any DB work happens.
 */
export const SignUpRequestSchema = z.object({
  organizationName: z.string().min(1).max(200),
  name: z.string().min(1).max(120),
  email: z
    .string()
    .email()
    .refine(isAllowedSignupEmail, {
      message: `Email must be at @${ALLOWED_SIGNUP_EMAIL_DOMAIN}.`,
    }),
  password: PasswordSchema,
  nmls: NmlsOptionalSchema,
});
export type SignUpRequest = z.infer<typeof SignUpRequestSchema>;

export const UpdateUserNmlsInputSchema = z.object({
  userId: z.string(),
  nmls: NmlsOptionalSchema,
});
export type UpdateUserNmlsInput = z.infer<typeof UpdateUserNmlsInputSchema>;

/**
 * Admin sets/clears a user's phone number. Used for MLO WhatsApp delivery —
 * the recipient resolver matches the loan's loan-officer NMLS to a User and
 * reads this phone. Empty string / null clears it. Stored verbatim; the
 * WhatsApp senders normalize to E.164 at send time.
 */
export const UpdateUserPhoneInputSchema = z.object({
  userId: z.string(),
  phone: z
    .string()
    .trim()
    .max(40)
    .nullable()
    .optional()
    .transform((v) => (v && v.length > 0 ? v : null)),
});
export type UpdateUserPhoneInput = z.infer<typeof UpdateUserPhoneInputSchema>;

/**
 * Admin sets another user's password directly (super-admin user management).
 * Enforces the same complex-password policy as signup.
 */
export const AdminSetPasswordInputSchema = z.object({
  userId: z.string(),
  password: PasswordSchema,
});
export type AdminSetPasswordInput = z.infer<typeof AdminSetPasswordInputSchema>;

/**
 * Admin activates or disables a user account. 'active' also clears a stuck
 * 'pending_verification' (lets an admin skip the email step). 'disabled' blocks
 * sign-in (enforced in the signin handler's status check).
 */
export const UpdateUserStatusInputSchema = z.object({
  userId: z.string(),
  status: z.enum(['active', 'disabled']),
});
export type UpdateUserStatusInput = z.infer<typeof UpdateUserStatusInputSchema>;

export const UpdateOwnProfileInputSchema = z.object({
  nmls: NmlsOptionalSchema,
});
export type UpdateOwnProfileInput = z.infer<typeof UpdateOwnProfileInputSchema>;

export const PasswordResetRequestSchema = z.object({
  email: z.string().email(),
});
export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>;
